var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useQuery } from '@tanstack/react-query';
import { fixedDareSounds, fixedPledgeSounds } from 'root/src/shared/constants/sounds';
import { CREATE_PROJECT, GET_ALERTS_CONFIG, GET_CUSTOM_SOUNDS } from 'root/src/shared/descriptions/endpoints/endpointIds';
import { useAppDispatch } from 'root/src/client/logic/helpers/useStore';
import setAlertsConfig from 'root/src/client/logic/creatorData/thunks/setAlertsConfig';
import testAlert from 'root/src/client/logic/creatorData/thunks/testAlert';
import addCustomSound from 'root/src/client/web/record/CreatorAlertsAudioPanel/thunks/addCustomSound';
import deleteCustomSound from 'root/src/client/web/record/CreatorAlertsAudioPanel/thunks/deleteCustomSound';
import pledgeOptionsHandler from 'root/src/client/web/record/CreatorAlertsAudioPanel/pledgeOptionsHandler';
import { uploadFileHandler } from 'root/src/client/logic/form/util/uploadFileHandler';
import computeSoundFileDuration from 'root/src/client/util/computeSoundFileDuration';
import { FileValidator } from 'root/src/client/v2/file/helpers/fileValidator';
import { dareDropClient } from 'root/src/client/v2/common/clients/dareDropClient';
import { globalQueryClient } from 'root/src/client/v2/common/clients/queryClient';
import { useAuthentication } from 'root/src/client/v2/auth/hooks/useAuthentication';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { Slider } from 'root/src/client/v2/common/components/ui/slider';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from 'root/src/client/v2/common/components/ui/tooltip';
import { Popover, PopoverTrigger, PopoverContent } from 'root/src/client/v2/common/components/ui/popover';
import { Skeleton } from 'root/src/client/v2/common/components/ui/skeleton';
import { toast } from 'root/src/client/v2/common/components/ui/sonner';
import { cn } from 'root/src/client/v2/common/helpers/cn';
const DARE_SOUNDS = Object.values(fixedDareSounds);
const PLEDGE_SOUNDS = Object.values(fixedPledgeSounds);
const POSITIONS = ['left', 'right'];
const clampVolume = (value) => Math.min(1, Math.max(0, value / 100));
// GET_CUSTOM_SOUNDS returns each type as an id-keyed object map; normalise to an array.
const toSoundArray = (value) => {
    if (Array.isArray(value)) {
        return value;
    }
    if (value && typeof value === 'object') {
        return Object.values(value);
    }
    return [];
};
const SoundCard = ({ name, selected, onClick }) => (<motion.button type="button" onClick={onClick} title={name} whileTap={{ scale: 0.97 }} className={cn('relative flex h-full min-h-[78px] w-full flex-col items-center justify-center gap-2 rounded-xl border border-solid p-3 text-xs font-medium transition-colors', selected
        ? 'border-accent/40 bg-accent/10 text-foreground'
        : 'border-border/50 bg-secondary/20 text-muted-foreground hover:border-border hover:text-foreground')}>
		<MusicNoteIcon sx={{ fontSize: 20 }} className={cn('shrink-0', selected && 'text-accent')}/>
		<span className="w-full truncate text-center">{name}</span>
		<AnimatePresence>
			{selected && (<motion.span initial={{ opacity: 0, scale: 0.4 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.4 }} transition={{ duration: 0.15, ease: 'easeOut' }} className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-accent shadow-sm">
					<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#15171c" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
						<path d="M20 6 9 17l-5-5"/>
					</svg>
				</motion.span>)}
		</AnimatePresence>
	</motion.button>);
const SOUND_FORMATS = ['mp3', 'aac', 'wav', 'aiff', 'flac', 'alac', 'wma', 'ogg', 'mqa', 'webm', 'rf64', 'gsm', 'ape', 'wv'];
const SoundPicker = ({ sounds, isSelected, onSelect }) => (<div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
		{sounds.map(sound => (<SoundCard key={sound.id} name={sound.name} selected={isSelected(sound)} onClick={() => onSelect(sound)}/>))}
	</div>);
const DeleteSoundButton = ({ soundName, inUse, loading, onConfirm }) => {
    const [open, setOpen] = useState(false);
    if (inUse) {
        return (<TooltipProvider delayDuration={300}>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button type="button" variant="ghost" size="icon" aria-disabled aria-label={`Delete ${soundName}`} className="h-9 w-9 shrink-0 cursor-not-allowed rounded-lg text-muted-foreground opacity-40">
							<DeleteOutlineIcon sx={{ fontSize: 18 }}/>
						</Button>
					</TooltipTrigger>
					<TooltipContent>Selected sound cannot be removed</TooltipContent>
				</Tooltip>
			</TooltipProvider>);
    }
    return (<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button type="button" variant="ghost" size="icon" loading={loading} aria-label={`Delete ${soundName}`} className="h-9 w-9 shrink-0 rounded-lg text-muted-foreground hover:text-foreground">
					<DeleteOutlineIcon sx={{ fontSize: 18 }}/>
				</Button>
			</PopoverTrigger>
			<PopoverContent align="end" className="w-60">
				<p className="mb-3 text-xs leading-relaxed text-foreground">
					Remove <span className="font-medium">{soundName}</span>? This cannot be undone.
				</p>
				<div className="flex justify-end gap-2">
					<Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)} className="h-7 rounded-md px-2.5 text-[11px]">
						Cancel
					</Button>
					<Button type="button" variant="destructive" size="sm" onClick={() => {
            setOpen(false);
            onConfirm();
        }} className="h-7 rounded-md px-2.5 text-[11px]">
						Remove
					</Button>
				</div>
			</PopoverContent>
		</Popover>);
};
const CustomSoundsManager = ({ sounds, adding, addId, deletingId, isInUse, onAdd, onDelete }) => (<div className="mt-3">
		<div className="mb-2 flex items-center justify-between gap-2">
			<span className="text-[11px] font-medium text-muted-foreground">Your custom sounds</span>
			<label htmlFor={addId} className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-solid border-border/60 px-2.5 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:border-border hover:text-foreground">
				<span className="flex h-4 w-4 items-center justify-center">
					{adding
        ? <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"/>
        : <AddIcon sx={{ fontSize: 16 }}/>}
				</span>
				Add sound
				<input id={addId} type="file" accept="audio/*" className="hidden" onChange={(event) => {
        var _a;
        const file = (_a = event.target.files) === null || _a === void 0 ? void 0 : _a[0];
        if (file) {
            onAdd(file);
        }
        event.target.value = '';
    }}/>
			</label>
		</div>
		<div className="flex flex-col gap-1.5">
			<AnimatePresence initial={false}>
				{sounds.map((sound) => {
        const inUse = isInUse(sound);
        return (<motion.div key={sound.id} layout initial={{ opacity: 0, y: -8, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.2, ease: 'easeOut' }} className="flex items-center gap-2.5 rounded-lg border border-solid border-border/40 bg-secondary/20 px-3 py-2">
							<MusicNoteIcon sx={{ fontSize: 16 }} className="shrink-0 text-muted-foreground"/>
							<span className="min-w-0 flex-1 truncate text-[11px]" title={sound.name}>{sound.name}</span>
							{inUse && <span className="shrink-0 text-[10px] font-medium text-muted-foreground/60">In use</span>}
							<DeleteSoundButton soundName={sound.name} inUse={inUse} loading={deletingId === sound.id} onConfirm={() => onDelete(sound)}/>
						</motion.div>);
    })}
			</AnimatePresence>
			{sounds.length === 0 && <p className="text-[11px] text-muted-foreground/50">No custom sounds yet.</p>}
		</div>
	</div>);
export const OBSAlertsSection = () => {
    const dispatch = useAppDispatch();
    const { authentication } = useAuthentication();
    const configQuery = useQuery({
        queryKey: [GET_ALERTS_CONFIG],
        queryFn: () => dareDropClient({ endpointId: GET_ALERTS_CONFIG }),
        enabled: !!authentication,
    }, globalQueryClient);
    const customSoundsQuery = useQuery({
        queryKey: [GET_CUSTOM_SOUNDS],
        queryFn: () => dareDropClient({ endpointId: GET_CUSTOM_SOUNDS }),
        enabled: !!authentication,
    }, globalQueryClient);
    const customSounds = customSoundsQuery.data;
    const customDareSounds = toSoundArray(customSounds === null || customSounds === void 0 ? void 0 : customSounds.dareSounds);
    const customPledgeSounds = toSoundArray(customSounds === null || customSounds === void 0 ? void 0 : customSounds.pledgeSounds);
    const dareList = [...DARE_SOUNDS, ...customDareSounds];
    const pledgeList = [...PLEDGE_SOUNDS, ...customPledgeSounds];
    const [dareSound, setDareSound] = useState(DARE_SOUNDS[0]);
    const [volume, setVolume] = useState(20);
    const [pledgeSounds, setPledgeSounds] = useState(PLEDGE_SOUNDS);
    const [pledgeOptions, setPledgeOptions] = useState([]);
    const [aligned, setAligned] = useState('left');
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [failed, setFailed] = useState(false);
    const [addingDare, setAddingDare] = useState(false);
    const [addingPledge, setAddingPledge] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const dirtyRef = useRef(false);
    const saveTimer = useRef(null);
    const audioRef = useRef(null);
    // Seed from the saved config once it loads (and after a save refetch). Does not flag dirty.
    useEffect(() => {
        var _a, _b, _c, _d, _e;
        const data = configQuery.data;
        if (!data) {
            return;
        }
        setDareSound((_b = (_a = data.dareSounds) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : DARE_SOUNDS[0]);
        setVolume((_c = data.soundVolume) !== null && _c !== void 0 ? _c : 20);
        setPledgeSounds((_d = data.pledgeSounds) !== null && _d !== void 0 ? _d : PLEDGE_SOUNDS);
        setPledgeOptions((_e = data.pledgeOptions) !== null && _e !== void 0 ? _e : []);
        setAligned(data.aligned === 'right' ? 'right' : 'left');
    }, [configQuery.data]);
    const save = () => {
        dirtyRef.current = false;
        setSaving(true);
        setFailed(false);
        const config = Object.assign(Object.assign({}, configQuery.data), { dareSounds: [dareSound], soundVolume: volume, pledgeSounds,
            pledgeOptions,
            aligned });
        const promise = Promise.resolve(dispatch(setAlertsConfig(config)));
        toast.promise(promise, {
            loading: 'Saving alert settings...',
            success: 'Alert settings saved',
            error: 'Could not save alert settings. Please try again.',
        });
        promise.then(() => {
            setSaving(false);
            setSaved(true);
            setTimeout(() => setSaved(false), 1300);
            globalQueryClient.invalidateQueries({ queryKey: [GET_ALERTS_CONFIG] });
        }, () => {
            setSaving(false);
            setFailed(true);
            setTimeout(() => setFailed(false), 1300);
        });
    };
    // Debounced auto-save on user edits; the Save button also commits immediately.
    useEffect(() => {
        if (!dirtyRef.current) {
            return undefined;
        }
        if (saveTimer.current) {
            clearTimeout(saveTimer.current);
        }
        saveTimer.current = setTimeout(save, 800);
        return () => {
            if (saveTimer.current) {
                clearTimeout(saveTimer.current);
            }
        };
    }, [dareSound, volume, pledgeSounds, pledgeOptions, aligned]);
    const markDirty = () => {
        dirtyRef.current = true;
    };
    const playPreview = (sound) => {
        if (audioRef.current) {
            audioRef.current.pause();
        }
        const audio = new Audio(sound.audioSrc);
        audio.volume = clampVolume(volume);
        audioRef.current = audio;
        audio.play().catch(() => undefined);
    };
    const selectDareSound = (sound) => {
        markDirty();
        setDareSound(sound);
        playPreview(sound);
    };
    const changeVolume = (value) => {
        markDirty();
        setVolume(value);
        if (audioRef.current) {
            // Update the currently playing preview live.
            audioRef.current.volume = clampVolume(value);
        }
    };
    const changeAligned = (position) => {
        markDirty();
        setAligned(position);
    };
    const togglePledge = (sound) => {
        markDirty();
        const isOn = pledgeSounds.some(item => item.id === sound.id);
        if (isOn) {
            setPledgeSounds(prev => prev.filter(item => item.id !== sound.id));
            setPledgeOptions(pledgeOptionsHandler({
                option: undefined, sound: undefined, addedSound: undefined, deletedSound: sound, pledgeOptions,
            }));
        }
        else if (pledgeSounds.length < 4) {
            setPledgeSounds(prev => [...prev, sound]);
            setPledgeOptions(pledgeOptionsHandler({
                option: undefined, sound: undefined, deletedSound: undefined, addedSound: sound, pledgeOptions,
            }));
            playPreview(sound);
        }
    };
    const handleSave = () => {
        if (saveTimer.current) {
            clearTimeout(saveTimer.current);
        }
        save();
    };
    const handleAddSound = (file, soundType) => {
        const setAdding = soundType === 'dareSounds' ? setAddingDare : setAddingPledge;
        setAdding(true);
        const toastId = toast.loading('Uploading sound...');
        uploadFileHandler({
            file,
            formats: SOUND_FORMATS,
            onSuccess: () => __awaiter(void 0, void 0, void 0, function* () {
                yield dispatch(addCustomSound({ name: file.name, file }, soundType));
                setAdding(false);
                toast.success('Sound added', { id: toastId });
                globalQueryClient.invalidateQueries({ queryKey: [GET_CUSTOM_SOUNDS] });
            }),
            onError: (text) => {
                setAdding(false);
                toast.error(text || 'Wrong file format: use mp3, wav, flac', { id: toastId });
            },
            constraints: [
                FileValidator.fileExtension(SOUND_FORMATS),
                {
                    validator: (candidate) => __awaiter(void 0, void 0, void 0, function* () { return (yield computeSoundFileDuration(candidate)) <= 10; }),
                    message: 'Sound must be at most 10 seconds long',
                },
            ],
        });
    };
    const handleDeleteSound = (sound, soundType) => __awaiter(void 0, void 0, void 0, function* () {
        setDeletingId(sound.id);
        if (soundType === 'dareSounds' && dareSound.id === sound.id) {
            setDareSound(DARE_SOUNDS[0]);
        }
        if (soundType === 'pledgeSounds') {
            setPledgeSounds(prev => prev.filter(item => item.id !== sound.id));
        }
        const toastId = toast.loading('Removing sound...');
        try {
            yield dispatch(deleteCustomSound(sound, soundType));
            toast.success('Sound removed', { id: toastId });
            globalQueryClient.invalidateQueries({ queryKey: [GET_CUSTOM_SOUNDS] });
        }
        catch (e) {
            toast.error('Could not remove sound', { id: toastId });
        }
        finally {
            setDeletingId(null);
        }
    });
    const sendTest = () => {
        dispatch(testAlert(CREATE_PROJECT));
        toast('Test alert sent', { description: 'Check your OBS Browser Source to hear it.' });
    };
    if (configQuery.isLoading) {
        return (<div className="space-y-4">
				<Skeleton className="h-5 w-32"/>
				<div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
					{DARE_SOUNDS.map(sound => <Skeleton key={sound.id} className="h-20 w-full rounded-xl"/>)}
				</div>
			</div>);
    }
    return (<div className="space-y-6">
			<div>
				<div className="mb-1 flex items-center justify-between gap-2">
					<h3 className="text-sm font-semibold">Alert Sound</h3>
					<Button variant="outline" size="sm" loading={saving} success={saved} error={failed} onClick={handleSave} className="h-8 min-w-[100px] rounded-lg text-[11px]">
						Save
					</Button>
				</div>
				<p className="mb-4 text-xs text-muted-foreground">Pick the sound that plays when a viewer completes a dare, then set its volume.</p>
				<SoundPicker sounds={dareList} isSelected={sound => (dareSound === null || dareSound === void 0 ? void 0 : dareSound.id) === sound.id} onSelect={selectDareSound}/>
				<CustomSoundsManager sounds={customDareSounds} adding={addingDare} addId="add-dare-sound" deletingId={deletingId} isInUse={sound => (dareSound === null || dareSound === void 0 ? void 0 : dareSound.id) === sound.id} onAdd={file => handleAddSound(file, 'dareSounds')} onDelete={sound => handleDeleteSound(sound, 'dareSounds')}/>
				<div className="mt-4">
					<div className="mb-1.5 flex items-center justify-between">
						<span className="text-[11px] font-medium text-muted-foreground">Volume</span>
						<span className="text-[11px] tabular-nums text-foreground">{volume}%</span>
					</div>
					<Slider min={0} max={100} step={1} value={[volume]} onValueChange={([value]) => changeVolume(value !== null && value !== void 0 ? value : 0)} aria-label="Alert volume"/>
				</div>
				<div className="mt-4">
					<Button variant="ghost" size="sm" onClick={sendTest} className="h-8 gap-1.5 rounded-lg px-3 text-[11px] text-muted-foreground hover:text-foreground">
						<VolumeUpIcon sx={{ fontSize: 14 }}/>
						Test sound
					</Button>
				</div>
			</div>

			<div>
				<h4 className="mb-1 text-xs font-semibold">Pledge sounds</h4>
				<p className="mb-3 text-[11px] text-muted-foreground">Choose up to 4 sounds that play for bit pledges.</p>
				<SoundPicker sounds={pledgeList} isSelected={sound => pledgeSounds.some(item => item.id === sound.id)} onSelect={togglePledge}/>
				<CustomSoundsManager sounds={customPledgeSounds} adding={addingPledge} addId="add-pledge-sound" deletingId={deletingId} isInUse={sound => pledgeSounds.some(item => item.id === sound.id)} onAdd={file => handleAddSound(file, 'pledgeSounds')} onDelete={sound => handleDeleteSound(sound, 'pledgeSounds')}/>
			</div>

			<div>
				<h4 className="mb-2 text-xs font-semibold">Text alert position</h4>
				<div className="inline-flex rounded-lg border border-solid border-border/50 bg-secondary/20 p-0.5">
					{POSITIONS.map(position => (<button key={position} type="button" onClick={() => changeAligned(position)} className={cn('rounded-md px-4 py-1.5 text-[11px] font-medium capitalize transition-colors', aligned === position ? 'bg-accent/15 text-accent' : 'text-muted-foreground hover:text-foreground')}>
							{position}
						</button>))}
				</div>
			</div>
		</div>);
};
